import Factory from '@leverege/factory'

import Filter from './Filter.js'
import Util from './Util.js'

const includes = ( value, filterValue ) => {
  return `${value}`.includes( filterValue )
}

export default class FilterController {
  /**
   * The FilterController manages the filter model state and performs filtering actions.
   * 
   * @param {string} stateKey The key within redux state where the filter model will be held
   * @param {object} lookup An object with fields ids as keys and accessors or objects with an accesor property as values
   * @param {object} options Options for the filter controller
   */
  constructor( stateKey, lookup, options = {} ) {
    const { type = 'default', idKey = 'id', idFunc } = options

    this.stateKey = stateKey
    this.lookup = lookup
    this.type = type
    this.idKey = idKey
    this.idFunc = idFunc
  }

  static cache = {}

  static FilterFactory = new Factory( {
    name : 'FilterFactory',
    registry : {
      includes,
      startsWith : ( value, filterValue ) => {
        return `${value}`.startsWith( filterValue )
      },
      equals : ( value, filterValue ) => {
        return value === filterValue
      }
    },
    defaultObject : includes,
    strategy : Factory.LOOKUP
  } )

  key( column ) {
    if ( typeof column === 'string' ) {
      return column
    }

    if ( this.idFunc ) {
      return this.idFunc( column )
    }

    return this.idKey ? column[this.idKey] : column && column.id
  }

  isColumnFiltered( column, filters ) {
    const key = this.key( column )
    const item = filters[key]

    return !!( item )
  }

  needsUpdate( data, filterModel ) {
    return FilterController.cache[`${this.stateKey}.lastData`] !== data ||
    FilterController.cache[`${this.stateKey}.lastFilterModel`] !== filterModel
  }

  filter( filterModel, data ) {
    if ( !this.needsUpdate( data, filterModel ) ) {
      return FilterController.cache[`${this.stateKey}.lastFilteredData`]
    }

    if ( filterModel && Object.keys( filterModel ).length !== 0 ) {
      const nData = this.applyFilters( data, filterModel )

      FilterController.cache[`${this.stateKey}.lastFilteredData`] = nData
      FilterController.cache[`${this.stateKey}.lastData`] = data
      FilterController.cache[`${this.stateKey}.lastFilterModel`] = filterModel

      return FilterController.cache[`${this.stateKey}.lastFilteredData`]
    }

    FilterController.cache[`${this.stateKey}.lastFilteredData`] = data
    FilterController.cache[`${this.stateKey}.lastData`] = data
    FilterController.cache[`${this.stateKey}.lastFilterModel`] = filterModel

    return data
  }

  applyFilters( data, filterModel ) {
    const filterFunc = ( item ) => {
      const keys = Object
        .keys( filterModel )

      for ( let i = 0; i < keys.length; i++ ) {
        const key = keys[i]
        const filterValue = filterModel[key].value
        const filter = FilterController.FilterFactory.create( filterModel[key] )
        const accessor = Util.getAccess( this.lookup[key] )
        const value = accessor( item )

        if ( !filter( value, filterValue ) ) {
          return false
        }
      }

      return true
    }

    return data.filter( filterFunc )
  }

  getFilterValue( column, filterModel ) {
    const key = this.key( column )
    const item = filterModel[key].value

    return item
  }

  set( filters ) {
    return Filter.set( {
      ...filters
    }, this.stateKey )
  }

  add( filters ) {
    return Filter.add( {
      ...filters
    }, this.stateKey )
  }

  remove( keys ) {
    const keysToRemove = typeof keys === 'string' ? [ keys ] : keys

    return Filter.remove( keysToRemove, this.stateKey )
  }

  clear() {
    return Filter.clear( this.stateKey )
  }

  clearAll() {
    return Filter.clearAll()
  }
}
